// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: simple-create save orchestration.
//
// Split out from simpleCreateHelpers.js so the pure validate / build
// helpers can be exercised from node:test without pulling in the
// axios client (which references window.sessionStorage at module-eval
// and crashes outside a browser).
//
// Two API hops, in order:
//   1. POST /api/erp/masters/items                  → returns item
//   2. PUT  /api/erp/masters/items/:id/<kind>-spec  → returns spec
//
// If (1) fails the modal surfaces the backend message and the user
// can retry — no row was created. If (1) succeeds but (2) fails, we
// return `extensionOk:false` so the modal can warn:
//   "Main item was saved, but {section} details could not be saved.
//    You can complete it from Advanced Edit."
// — exactly the wording the Stage 6B.8A-3C brief uses.
//
// `deps.itemsApi` lets tests swap in a mock without monkey-patching
// the real client. In production code the modal calls
// saveSimpleItem(kind, values) with no deps and gets the real client.
// ─────────────────────────────────────────────────────────────────

import { itemsApi as defaultItemsApi } from './itemsApi';
import { buildPayloadsFor, SPEC_UPSERT_METHOD_BY_KIND } from './simpleCreateHelpers';

export async function saveSimpleItem(kind, values, deps = {}) {
  const itemsApi  = deps.itemsApi || defaultItemsApi;
  const editId    = deps.editId || null;
  const setPayload = deps.setPayload || null;   // { setType, setName, components, ... } or null
  const { core, extension } = buildPayloadsFor(kind, values);

  // Step 1 — create OR update the core row. Errors propagate to the
  // modal so the user sees the backend's friendly message.
  const item = editId
    ? await itemsApi.update(editId, core)
    : await itemsApi.create(core);

  // Step 2 — upsert the kind extension. Catch + return the warning so
  // the modal can stay open / point the user at Advanced Edit.
  const methodName = SPEC_UPSERT_METHOD_BY_KIND[kind];
  let extensionOk    = true;
  let extensionError = null;
  if (methodName && extension && Object.keys(extension).length > 0) {
    const upsert = itemsApi[methodName];
    if (typeof upsert === 'function') {
      try {
        await upsert.call(itemsApi, item.id, extension);
      } catch (err) {
        extensionOk    = false;
        extensionError = err;
      }
    }
  }

  // Step 3 — Stage 6B.8A-3H: optional set / kit upsert OR delete.
  //   setPayload truthy with components  → PUT /:id/set
  //   setPayload === { _clear: true }    → DELETE /:id/set (toggle off
  //                                         the "Hardware Set" switch
  //                                         on an item that previously
  //                                         had a set).
  if (setPayload && typeof itemsApi.upsertItemSet === 'function') {
    try {
      if (setPayload._clear === true) {
        try {
          await itemsApi.deleteItemSet(item.id);
        } catch (delErr) {
          // 404 just means there was nothing to delete; quietly succeed.
          const status = delErr && delErr.response && delErr.response.status;
          if (status && status !== 404) throw delErr;
        }
      } else {
        await itemsApi.upsertItemSet(item.id, setPayload);
      }
    } catch (err) {
      extensionOk    = false;
      extensionError = err;
    }
  }

  return { item, extensionOk, extensionError };
}
